/**
 * Shared TanStack Router `validateSearch` schema for the workspace surfaces.
 *
 * Deep-link contract (matches `decisionToSearchParams`):
 *
 *   /workspace/finance?sub=banking&focus=invoice-123&filter=overdue
 *
 * Why a single schema instead of one bespoke per tab?
 *   - DRY: every workspace surface accepts the same URL contract today
 *     (sub/focus + free-form passthrough filters). When a tab grows
 *     a strongly-typed param, extend this schema with `.extend({ ... })`
 *     at that route's call site — the base shape stays shared.
 *   - Cross-cuts the router store: `useWorkspaceRouter.navigateToDecision`
 *     writes exactly these keys, and the workspace routes read them back
 *     via `useSearch()`. Round-trip is type-safe.
 *
 * Note: filters arrive as plain strings on the URL (URL-search is string-
 * native). Consumers that need richer types must `JSON.parse(filters)`
 * themselves — kept loose here so the schema doesn't shut the door on
 * unrelated query params (e.g. third-party deep links).
 *
 * May 26, 2026 — ``rail=canvas|dashboard`` param removed: Canvas + Dashboard
 * promoted to first-class WorkspaceTabs, so any deep-link to those
 * surfaces uses ``/workspace/canvases`` or ``/workspace/dashboard``
 * directly instead of a secondary ``?rail=`` query.
 */

import { z } from "zod";

/**
 * Tolerant string coercion — TanStack Router decodes URL search params as
 * strings by default, but a `navigate({ search: { focus: <value> } })`
 * call from JS code can pass a number/null/undefined when the upstream
 * source is loosely-typed (e.g. BE returns Gmail ``message_id`` as int64
 * that JSON.parse coerces to ``number``, May 22, 2026 incident: clicking
 * a Word doc in the hub fired ``Invalid input`` Zod error on ``focus``
 * field). Accept either, coerce to string, drop ``null`` /
 * ``undefined`` — they collapse to "absent" which is what the optional
 * marker already means.
 */
const TolerantStringSchema = z.preprocess((v) => {
  if (v === null || v === undefined) return undefined;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "bigint") return String(v);
  if (typeof v === "boolean") return String(v);
  return undefined;
}, z.string().optional());

export const WorkspaceSearchSchema = z
  .object({
    /** Sub-tab id within the workspace (e.g. `"sdi"` inside finance). */
    sub: TolerantStringSchema,
    /** Optional record id to focus inside the surface (e.g. an invoice). */
    focus: TolerantStringSchema,
    /** Optional generic filter id (sub-bucket within a sub-tab). */
    filter: TolerantStringSchema,
    /**
     * Optional chat message id propagated by ``useSourceAutoOpen`` /
     * ``SourceRailSurface`` so the focused viewer can resolve the
     * cross-source neighbour set. Same tolerance as ``focus`` because
     * the same upstream paths feed it.
     */
    msg: TolerantStringSchema,
    /**
     * Optional HITL action proposal id. When present, the workspace
     * shell mounts a ``FocusedActionView`` over the routed hub — same
     * chrome as ``CalendarEventPane`` / ``InboxDetailPane`` (rich
     * header + body + sticky footer). Set by ``useActionRouter`` when
     * a new ``action_proposal`` lands AND the user is not already
     * inside a focused detail (``?focus=`` absent). Clearing this param
     * via the back chevron returns the user to the routed hub list
     * without dismissing the proposal (it stays pending in the store).
     */
    proposal: TolerantStringSchema,
  })
  .passthrough();

export type WorkspaceSearch = z.infer<typeof WorkspaceSearchSchema>;

/**
 * TanStack Router accepts a parser fn for `validateSearch`. This is the
 * fn shape every workspace route imports — keeps the call sites tidy:
 *
 *   export const Route = createFileRoute(...)({
 *     validateSearch: validateWorkspaceSearch,
 *     component: ...,
 *   });
 */
export function validateWorkspaceSearch(
  raw: Record<string, unknown>,
): WorkspaceSearch {
  return WorkspaceSearchSchema.parse(raw);
}
